import { useMemo, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import { formatCurrency } from '../../data/constants';
import { getExpectedFulfillmentDate, dateKeyLocal } from '../../utils/orderFulfillmentDate';
import Loader from '../utils/Loader';

const CALENDAR_STATUSES = new Set([
  'Active',
  'Out for Delivery',
  'Ready for Pickup',
  'Delivered',
  'Picked Up',
  'Past Due',
]);

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}

function buildMonthCells(visibleMonth) {
  const first = startOfMonth(visibleMonth);
  const year = first.getFullYear();
  const month = first.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  // Week starts Monday (1) — common for business / ops tools
  let startWeekday = first.getDay();
  const mondayBased = startWeekday === 0 ? 6 : startWeekday - 1;
  const cells = [];
  for (let i = 0; i < mondayBased; i += 1) {
    cells.push({ type: 'pad' });
  }
  for (let day = 1; day <= lastDay; day += 1) {
    cells.push({ type: 'day', date: new Date(year, month, day) });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ type: 'pad' });
  }
  return cells;
}

function googleCalendarUrl({ title, details, start, end }) {
  const fmt = (dt) =>
    dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    details: details || '',
    dates: `${fmt(start)}/${fmt(end)}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SellerOrdersCalendar = ({ orders, currency, exchangeRate, amountToGet, onSelectOrder, isLoading }) => {
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));
  const [selectedKey, setSelectedKey] = useState(() => dateKeyLocal(new Date()));

  const { byDate, undated, overdueIds } = useMemo(() => {
    const map = new Map();
    const undatedList = [];
    const overdue = new Set();
    const now = Date.now();

    const list = (orders || []).filter((o) => CALENDAR_STATUSES.has(o.currentStatus));

    list.forEach((order) => {
      const expected = getExpectedFulfillmentDate(order);
      if (!expected) {
        undatedList.push(order);
        return;
      }
      const key = dateKeyLocal(expected);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(order);
      if (expected.getTime() < now && CALENDAR_STATUSES.has(order.currentStatus)) {
        if (['Active', 'Out for Delivery', 'Ready for Pickup', 'Past Due'].includes(order.currentStatus)) {
          overdue.add(order._id);
        }
      }
    });

    map.forEach((arr, k) => {
      arr.sort((a, b) => getExpectedFulfillmentDate(a) - getExpectedFulfillmentDate(b));
    });
    undatedList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return { byDate: map, undated: undatedList, overdueIds: overdue };
  }, [orders]);

  const monthCells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);

  const monthLabel = visibleMonth.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const selectedOrders = selectedKey ? byDate.get(selectedKey) || [] : [];
  const todayKey = dateKeyLocal(new Date());

  const soonestDue = useMemo(() => {
    const rows = [];
    byDate.forEach((list) => {
      list.forEach((o) => {
        const date = getExpectedFulfillmentDate(o);
        if (date) rows.push({ order: o, date });
      });
    });
    rows.sort((a, b) => a.date - b.date);
    return rows.slice(0, 15);
  }, [byDate]);

  const exportIcsMonth = () => {
    const y = visibleMonth.getFullYear();
    const m = visibleMonth.getMonth();
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Chocowi//Seller Orders//EN'];
    const monthPrefix = `${y}-${String(m + 1).padStart(2, '0')}`;
    byDate.forEach((list, key) => {
      if (!key.startsWith(monthPrefix)) return;
      list.forEach((order) => {
        const exp = getExpectedFulfillmentDate(order);
        if (!exp) return;
        const uid = `${order._id}@chocowi`;
        const dayStr = `${exp.getFullYear()}${String(exp.getMonth() + 1).padStart(2, '0')}${String(exp.getDate()).padStart(2, '0')}`;
        const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        lines.push('BEGIN:VEVENT');
        lines.push(`UID:${uid}`);
        lines.push(`DTSTAMP:${stamp}`);
        lines.push(`DTSTART;VALUE=DATE:${dayStr}`);
        lines.push(`DTEND;VALUE=DATE:${dayStr}`);
        lines.push(`SUMMARY:${(order.productInfo?.title || 'Order').replace(/\n/g, ' ')} (#${order.customOrderId})`);
        lines.push('END:VEVENT');
      });
    });
    lines.push('END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seller-orders-${y}-${String(m + 1).padStart(2, '0')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderOrderRow = (order) => {
    const expected = getExpectedFulfillmentDate(order);
    const isOd = overdueIds.has(order._id);
    const typeLabel =
      order.productInfo?.listingType === 'service'
        ? 'Service'
        : order.isB2B
          ? 'B2B'
          : 'Product';
    const gHref =
      expected &&
      googleCalendarUrl({
        title: `${order.productInfo?.title || 'Order'} (#${order.customOrderId})`,
        details: `Order ${order.customOrderId} — ${order.buyerInfo?.fullName || 'Buyer'}`,
        start: expected,
        end: new Date(expected.getTime() + 60 * 60 * 1000),
      });

    return (
      <button
        type="button"
        key={order._id}
        className={`sellerCalOrderRow${isOd ? ' sellerCalOrderRow--overdue' : ''}`}
        onClick={() => onSelectOrder(order)}
      >
        <span className="sellerCalOrderRowMain">
          <span className="sellerCalOrderTitle">{order.productInfo?.title || '—'}</span>
          <span className="sellerCalOrderMeta">
            #{order.customOrderId} · {order.buyerInfo?.fullName || 'Buyer'} ·{' '}
            <span className="sellerCalTypePill">{typeLabel}</span>
          </span>
        </span>
        <span className="sellerCalOrderRowRight">
          <span className={isOd ? 'sellerCalDue sellerCalDue--late' : 'sellerCalDue'}>
            {order.currentStatus}
          </span>
          <span className="sellerCalAmt">{formatCurrency(amountToGet(order.summary), currency, exchangeRate)}</span>
          {gHref && (
            <a
              className="sellerCalGcal"
              href={gHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Add to Google Calendar"
              onClick={(e) => e.stopPropagation()}
            >
              <FaExternalLinkAlt size={12} />
            </a>
          )}
        </span>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="sellerOrdersCalendar sellerOrdersCalendar--loading">
        <Loader type="simpleMini" />
        <span className="sellerCalLoadingText">Loading delivery calendar…</span>
      </div>
    );
  }

  return (
    <div className="sellerOrdersCalendar">
      <div className="sellerOrdersCalendarHeader">
        <div className="sellerOrdersCalendarTitleBlock">
          <h2 className="sellerOrdersCalendarTitle">Delivery calendar</h2>
          <p className="sellerOrdersCalendarSubtitle">
            Expected delivery or pickup dates for in-progress orders. Click a day to see orders due then, or open an
            order to manage it.
          </p>
        </div>
        <div className="sellerOrdersCalendarToolbar">
          <button type="button" className="sellerCalBtn sellerCalBtn--ghost" onClick={exportIcsMonth}>
            Download .ics (this month)
          </button>
        </div>
      </div>

      <div className="sellerOrdersCalendarLayout">
        <div className="sellerOrdersCalendarCard">
          <div className="sellerCalMonthNav">
            <button
              type="button"
              className="sellerCalIconBtn"
              aria-label="Previous month"
              onClick={() => setVisibleMonth((d) => addMonths(d, -1))}
            >
              <FaChevronLeft size={14} />
            </button>
            <span className="sellerCalMonthLabel">{monthLabel}</span>
            <button
              type="button"
              className="sellerCalIconBtn"
              aria-label="Next month"
              onClick={() => setVisibleMonth((d) => addMonths(d, 1))}
            >
              <FaChevronRight size={14} />
            </button>
            <button
              type="button"
              className="sellerCalTodayBtn"
              onClick={() => {
                const n = new Date();
                setVisibleMonth(startOfMonth(n));
                setSelectedKey(dateKeyLocal(n));
              }}
            >
              Today
            </button>
          </div>

          <div className="sellerCalWeekdays">
            {WEEKDAYS.map((w) => (
              <div key={w} className="sellerCalWeekday">
                {w}
              </div>
            ))}
          </div>

          <div className="sellerCalGrid">
            {monthCells.map((cell, idx) => {
              if (cell.type === 'pad') {
                return <div key={`pad-${idx}`} className="sellerCalCell sellerCalCell--pad" />;
              }
              const key = dateKeyLocal(cell.date);
              const count = (byDate.get(key) || []).length;
              const isToday = key === todayKey;
              const isSelected = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={`sellerCalCell sellerCalCell--day${isToday ? ' sellerCalCell--today' : ''}${
                    isSelected ? ' sellerCalCell--selected' : ''
                  }`}
                  onClick={() => setSelectedKey(key)}
                >
                  <span className="sellerCalDayNum">{cell.date.getDate()}</span>
                  {count > 0 && <span className="sellerCalBadge">{count > 9 ? '9+' : count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="sellerOrdersCalendarSide">
          <div className="sellerCalSideCard">
            <h3 className="sellerCalSideTitle">
              {selectedKey
                ? new Date(selectedKey + 'T12:00:00').toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'Select a date'}
            </h3>
            {selectedOrders.length === 0 ? (
              <p className="sellerCalEmpty">No orders due on this date.</p>
            ) : (
              <div className="sellerCalOrderList">{selectedOrders.map((o) => renderOrderRow(o))}</div>
            )}
          </div>

          <div className="sellerCalSideCard">
            <h3 className="sellerCalSideTitle">Soonest due first</h3>
            {soonestDue.length === 0 ? (
              <p className="sellerCalEmpty">No scheduled orders in view.</p>
            ) : (
              <div className="sellerCalOrderList">
                {soonestDue.map(({ order }) => (
                  <div key={order._id}>{renderOrderRow(order)}</div>
                ))}
              </div>
            )}
          </div>

          {undated.length > 0 && (
            <div className="sellerCalSideCard sellerCalSideCard--muted">
              <h3 className="sellerCalSideTitle">No computed due date</h3>
              <p className="sellerCalHint">
                These orders have no buyer-requested date and no parseable lead time — open the order to see details.
              </p>
              <div className="sellerCalOrderList">{undated.map((o) => renderOrderRow(o))}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerOrdersCalendar;
